// Cámara dentro de la app: siempre en vivo (no se puede elegir de la galería ni de archivos); flash, zoom y vista previa con recorte.
// Se abre con abrirCamara({ multiple }) → Promise<File[]> (vacío si se cancela). <CamaraHost /> va una sola vez en App.
// Varias fotos: se sacan seguidas, arriba se ve la cantidad y «Usar» las carga todas de una vez.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, RefreshCw, X, Zap, ZapOff, Loader2 } from 'lucide-react';
import { buscarCamaraConFlash, dejarCamaraLista, pedirCamara } from './camaraStream';
import { BarraZoom, usePellizco, useZoom } from './camaraZoom';
import { RevisarFoto } from './RevisarFoto';

let abrirGlobal = null;
export const abrirCamara = ({ multiple = false } = {}) =>
  new Promise((resolve) => (abrirGlobal ? abrirGlobal({ multiple, resolve }) : resolve([])));

const ERRORES = {
  NotAllowedError: 'Permití el acceso a la cámara en el navegador para tomar fotos.',
  NotFoundError: 'No se encontró una cámara en este equipo.',
  NotReadableError: 'La cámara está ocupada por otra aplicación. Cerrala e intentá de nuevo.',
};
const MAX_LADO = 1600;

const capturar = (video, z = 1) => new Promise((resolve) => {
  // Zoom digital: se guarda exactamente lo que se ve en pantalla (el video va «contain» y escalado desde el centro)
  const { videoWidth: vw, videoHeight: vh, clientWidth: cw, clientHeight: ch } = video;
  const s = cw && ch ? Math.min(cw / vw, ch / vh) * z : 0;
  const w = s ? Math.min(vw, cw / s) : vw / z;
  const h = s ? Math.min(vh, ch / s) : vh / z;
  const k = Math.min(1, MAX_LADO / Math.max(w, h)); // sale ya en el tamaño final: guardar es más rápido
  const c = document.createElement('canvas');
  c.width = Math.round(w * k);
  c.height = Math.round(h * k);
  c.getContext('2d').drawImage(video, (vw - w) / 2, (vh - h) / 2, w, h, 0, 0, c.width, c.height);
  c.toBlob((b) => resolve(b ? new File([b], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' }) : null), 'image/jpeg', 0.88);
});

const useStream = (videoRef, frente, recarga) => {
  const [track, setTrack] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let vivo = true;
    setTrack(null);
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) { setError('Este navegador no permite usar la cámara.'); return undefined; }
    pedirCamara(frente)
      .then((s) => {
        if (!vivo) return;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
        setTrack(s.getVideoTracks()[0] || null);
      })
      .catch((e) => vivo && setError(ERRORES[e?.name] || 'No se pudo abrir la cámara.'));
    return () => { vivo = false; };
  }, [videoRef, frente, recarga]);
  useEffect(() => () => dejarCamaraLista(), []);
  return { track, error };
};

// Flash: siempre a mano en la cámara trasera; si la elegida no tiene linterna se busca la que sí
const useFlash = (track, recargar) => {
  const [flash, setFlash] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState('');
  const pendiente = useRef(false);
  const avisar = (t) => { setAviso(t); setTimeout(() => setAviso(''), 3500); };
  useEffect(() => {
    setFlash(false);
    if (track && pendiente.current && track.getCapabilities?.()?.torch) {
      pendiente.current = false;
      track.applyConstraints({ advanced: [{ torch: true }] }).then(() => setFlash(true)).catch(() => {});
    }
  }, [track]);
  const alternar = async () => {
    const v = !flash;
    if (track?.getCapabilities?.()?.torch) {
      track.applyConstraints({ advanced: [{ torch: v }] }).then(() => setFlash(v)).catch(() => avisar('No se pudo prender el flash'));
      return;
    }
    if (!v) { setFlash(false); return; }
    setBuscando(true);
    const ok = await buscarCamaraConFlash().catch(() => false);
    setBuscando(false);
    pendiente.current = ok;
    if (!ok) avisar('Este equipo no permite prender el flash desde la app');
    recargar();
  };
  return { flash, alternar, buscando, aviso };
};

const BotonRedondo = ({ onClick, children, testid, label, activo }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testid}
    className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${activo ? 'bg-yellow-400 text-black' : 'bg-white/15 text-white hover:bg-white/25'}`}>
    {children}
  </button>
);

const Controles = ({ frente, fl, disparar, puede, girar }) => (
  <div className="flex items-center justify-around px-6 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
    {!frente ? (
      <BotonRedondo onClick={fl.alternar} testid="camara-flash" label="Flash" activo={fl.flash}>{fl.flash ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}</BotonRedondo>
    ) : <span className="h-12 w-12" />}
    <button type="button" onClick={disparar} disabled={!puede} aria-label="Tomar foto" data-testid="camara-disparar"
      className="h-[72px] w-[72px] rounded-full border-4 border-white bg-white/90 flex items-center justify-center active:scale-95 disabled:opacity-40">
      <Camera className="h-7 w-7 text-gray-800" />
    </button>
    <BotonRedondo onClick={girar} testid="camara-girar" label="Cambiar cámara"><RefreshCw className="h-5 w-5" /></BotonRedondo>
  </div>
);

// Varias fotos: cantidad + «Usar» arriba y las miniaturas (tocar una para recortarla o quitarla)
const BarraVarias = ({ fotos, onUsar, onRevisar }) => (fotos.length ? (
  <>
    <button type="button" onClick={onUsar} data-testid="camara-usar-todas"
      className="absolute top-3 left-3 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 px-4 text-sm font-semibold text-white flex items-center gap-2 shadow-lg active:scale-95">
      <Check className="h-4 w-4" /> Usar <span data-testid="camara-contador">{fotos.length} foto{fotos.length === 1 ? '' : 's'}</span>
    </button>
    <div className="absolute top-16 left-3 right-3 flex gap-2 overflow-x-auto pb-1" data-testid="camara-miniaturas">
      {fotos.map((f, i) => (
        <button key={f.url} type="button" onClick={() => onRevisar(i)} data-testid={`camara-miniatura-${i}`}
          className="h-14 w-14 shrink-0 overflow-hidden rounded-md border-2 border-white/80 shadow"><img src={f.url} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" /></button>
      ))}
    </div>
  </>
) : null);

const CamaraVista = ({ multiple, onListo }) => {
  const videoRef = useRef(null);
  const [frente, setFrente] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const { track, error } = useStream(videoRef, frente, recarga);
  const [listo, setListo] = useState(false);
  const [fotos, setFotos] = useState([]); // [{ file, url }]
  const [destello, setDestello] = useState(false);
  const [rev, setRev] = useState(null); // foto en vista previa ({ file, url, i } si es una de varias)
  const fl = useFlash(track, () => setRecarga((x) => x + 1));
  const z = useZoom(track);
  const pellizco = usePellizco(z.zoom, z.setZoom);
  // Con la cámara ya encendida el video puede estar listo antes que este efecto
  useEffect(() => { const v = videoRef.current; setListo(!!(track && v?.videoWidth && v.readyState >= 2)); }, [track]);

  const disparar = useCallback(async () => {
    const v = videoRef.current;
    if (!v?.videoWidth || rev) return;
    if (!multiple) v.pause(); // la imagen se congela al instante
    setDestello(true);
    setTimeout(() => setDestello(false), 120);
    const f = await capturar(v, z.digital ? z.zoom : 1);
    if (!f) { v.play().catch(() => {}); return; }
    const foto = { file: f, url: URL.createObjectURL(f) };
    if (multiple) setFotos((x) => [...x, foto]);
    else setRev(foto);
  }, [rev, multiple, z.digital, z.zoom]);
  const salir = (files) => { fotos.forEach((f) => URL.revokeObjectURL(f.url)); if (rev) URL.revokeObjectURL(rev.url); onListo(files); };
  const repetir = () => { URL.revokeObjectURL(rev.url); setRev(null); videoRef.current?.play().catch(() => {}); };
  // Revisando una de varias: Listo (con o sin recorte) la reemplaza; Quitar la saca
  const cambiar = (file) => {
    if (file !== rev.file) {
      URL.revokeObjectURL(rev.url);
      setFotos((x) => x.map((f, i) => (i === rev.i ? { file, url: URL.createObjectURL(file) } : f)));
    }
    setRev(null);
  };
  const quitar = () => { URL.revokeObjectURL(rev.url); setFotos((x) => x.filter((_, i) => i !== rev.i)); setRev(null); };

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black flex flex-col pointer-events-auto" data-testid="camara-en-vivo" data-camara-en-vivo=""
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); salir([]); } }}>
      <div className="relative flex-1 overflow-hidden touch-none" {...pellizco} data-testid="camara-visor">
        <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-contain" data-testid="camara-video"
          style={z.digital && z.zoom > 1 ? { transform: `scale(${z.zoom})` } : undefined}
          onLoadedData={(e) => setListo(e.currentTarget.videoWidth > 0)} onPlaying={(e) => setListo(e.currentTarget.videoWidth > 0)} />
        {destello && <div className="absolute inset-0 bg-white/70" />}
        {track && listo && <BarraZoom {...z} />}
        {(!track || !listo || fl.buscando) && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm bg-black/30">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {fl.buscando ? 'Buscando la cámara con flash…' : 'Abriendo cámara…'}
          </div>
        )}
        {error && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white text-sm" data-testid="camara-error">{error}</div>}
        {fl.aviso && <div className="absolute top-28 left-1/2 -translate-x-1/2 rounded-full bg-black/75 px-4 py-2 text-xs text-white text-center" data-testid="camara-aviso">{fl.aviso}</div>}
        {multiple && <BarraVarias fotos={fotos} onUsar={() => salir(fotos.map((f) => f.file))} onRevisar={(i) => setRev({ ...fotos[i], i })} />}
        <button type="button" onClick={() => salir([])} aria-label="Cerrar cámara" data-testid="camara-cerrar"
          className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
      </div>
      <Controles frente={frente} fl={fl} disparar={disparar} puede={!!track && listo && !rev && !fl.buscando} girar={() => setFrente((x) => !x)} />
      {rev && (rev.i === undefined
        ? <RevisarFoto key={rev.url} foto={rev} onUsar={(f) => salir([f])} onDescartar={repetir} />
        : <RevisarFoto key={rev.url} foto={rev} revisando onUsar={cambiar} onDescartar={quitar} />)}
    </div>,
    document.body,
  );
};

export const CamaraHost = () => {
  const [pedido, setPedido] = useState(null);
  useEffect(() => { abrirGlobal = setPedido; return () => { abrirGlobal = null; }; }, []);
  if (!pedido) return null;
  return <CamaraVista multiple={pedido.multiple} onListo={(files) => { pedido.resolve(files); setPedido(null); }} />;
};
